import { Button, Card, Container, Group, Stack, Table, Text, ThemeIcon, Title } from '@mantine/core'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuSignature } from 'react-icons/lu'
import { CurrencyText } from '@/frontend/components/marina/CurrencyText'
import { DataTable } from '@/frontend/components/marina/DataTable'
import { MoneyText } from '@/frontend/components/marina/MoneyText'
import { PillNav } from '@/frontend/components/marina/PillNav'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import { useCanWrite } from '@/frontend/hooks/useAuth'
import { type ContractStatus, useContractStatusCounts, useContracts } from '@/frontend/hooks/useContracts'

type ContractTab = 'ALL' | ContractStatus

const VALID_CONTRACT_TABS: ContractTab[] = [
  'ALL',
  'DRAFT',
  'PREPARED',
  'PENDING_REVIEW',
  'ACTIVE',
  'EXPIRED',
  'TERMINATED',
]

export const Route = createFileRoute('/dashboard/accounts/contracts/')({
  validateSearch: (s: Record<string, unknown>): { tab?: ContractTab } => ({
    tab: VALID_CONTRACT_TABS.includes(s.tab as ContractTab) ? (s.tab as ContractTab) : undefined,
  }),
  component: ContractsList,
})

function ContractsList() {
  const { t } = useTranslation()
  const navigate = useNavigate({ from: Route.fullPath })
  const { tab: rawTab } = Route.useSearch()
  const tab = rawTab ?? 'ALL'
  const setTab = (v: ContractTab) => navigate({ search: () => ({ tab: v }), replace: true })
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 15
  const canWrite = useCanWrite()

  const { data: countsData } = useContractStatusCounts()
  const counts = countsData?.counts ?? {}
  const totalAll = Object.values(counts).reduce((s, n) => s + n, 0)
  const STATUS_TABS: { value: ContractTab; label: string; count?: number; countColor?: string }[] = [
    { value: 'ALL', label: t('page.accounts.contracts.tab_all'), count: totalAll || undefined, countColor: 'gray' },
    {
      value: 'DRAFT',
      label: t('page.accounts.contracts.tab_draft'),
      count: counts.DRAFT || undefined,
      countColor: 'gray',
    },
    {
      value: 'PREPARED',
      label: t('page.accounts.contracts.tab_prepared'),
      count: counts.PREPARED || undefined,
      countColor: 'blue',
    },
    {
      value: 'PENDING_REVIEW',
      label: t('page.accounts.contracts.tab_review'),
      count: counts.PENDING_REVIEW || undefined,
      countColor: 'orange',
    },
    {
      value: 'ACTIVE',
      label: t('page.accounts.contracts.tab_active'),
      count: counts.ACTIVE || undefined,
      countColor: 'teal',
    },
    {
      value: 'EXPIRED',
      label: t('page.accounts.contracts.tab_expired'),
      count: counts.EXPIRED || undefined,
      countColor: 'orange',
    },
    {
      value: 'TERMINATED',
      label: t('page.accounts.contracts.tab_terminated'),
      count: counts.TERMINATED || undefined,
      countColor: 'red',
    },
  ]

  const { data, isLoading } = useContracts({
    q: query,
    status: tab === 'ALL' ? undefined : tab,
    page,
    pageSize,
  })
  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="laut" size="lg" radius="md">
              <LuSignature size={18} />
            </ThemeIcon>
            <div>
              <Title order={2} ff="var(--font-display)" fw={600}>
                {t('page.accounts.contracts.title')}
              </Title>
              <Text size="xs" c="dimmed">
                {t('page.accounts.contracts.subtitle')}
              </Text>
            </div>
          </Group>
          <Group gap="sm" align="center">
            <input
              type="search"
              value={search}
              placeholder={t('page.accounts.contracts.placeholder')}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setQuery(search)
                  setPage(1)
                }
              }}
              style={{
                fontSize: '0.8rem',
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid var(--mantine-color-default-border)',
                background: 'var(--app-card-bg)',
                color: 'inherit',
                width: 200,
                outline: 'none',
              }}
            />
            {canWrite && (
              <Button color="karang" size="sm" component={Link} to="/dashboard/accounts/contracts/new">
                {t('page.accounts.contracts.btn_new')}
              </Button>
            )}
          </Group>
        </Group>

        <PillNav
          value={tab}
          onChange={(v) => {
            setTab(v)
            setPage(1)
          }}
          items={STATUS_TABS}
        />

        <Card radius="md" padding="lg">
          <DataTable
            isEmpty={!isLoading && items.length === 0}
            emptyLabel={t('page.accounts.contracts.empty')}
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            columns={
              <>
                <Table.Th>{t('page.accounts.contracts.col_code')}</Table.Th>
                <Table.Th>{t('page.accounts.contracts.col_customer')}</Table.Th>
                <Table.Th>{t('page.accounts.contracts.col_boat')}</Table.Th>
                <Table.Th>{t('page.accounts.contracts.col_berth')}</Table.Th>
                <Table.Th>{t('page.accounts.contracts.col_duration')}</Table.Th>
                <Table.Th ta="right">{t('page.accounts.contracts.col_value')}</Table.Th>
                <Table.Th>{t('page.accounts.contracts.col_status')}</Table.Th>
              </>
            }
          >
            {items.map((c) => {
              const customerName = c.customer ? `${c.customer.firstName} ${c.customer.lastName}`.trim() : '—'
              const start = dayjs(c.startDate)
              const end = dayjs(c.endDate)
              const months = end.diff(start, 'month')
              const duration =
                months >= 1
                  ? t('page.accounts.contracts.duration_months', { n: months })
                  : t('page.accounts.contracts.duration_days', { n: end.diff(start, 'day') })

              return (
                <Table.Tr
                  key={c.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate({ to: `/dashboard/accounts/contracts/${c.id}` as any })}
                >
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {c.code}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {start.format('DD MMM YYYY')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{customerName}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{c.boat?.name ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{c.berth?.code ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{duration}</Text>
                    <Text size="xs" c="dimmed">
                      {c.type}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <MoneyText amount={c.totalValueIdr} currency={c.currency} fw={500} />
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge status={c.status} kind="contract" />
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </DataTable>
        </Card>
      </Stack>
    </Container>
  )
}
